import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark, faStar } from '@fortawesome/free-solid-svg-icons';
import { useDemoProjects, PLAN_LABEL } from '../components/demoProjects';
import './FeedbackModal.css';

const API = process.env.REACT_APP_API_BASE_URL;

const STATUS = [
  { value: 0, label: 'Class completed' },
  { value: 1, label: 'Student not joined' },
  { value: 2, label: 'Student left early' },
  { value: 3, label: 'Not interested' },
];

const DemoFeedbackModal = ({ feedback, onClose, onSubmitSuccess }) => {
  const [form, setForm] = useState({
    IdClassStatus: 0,
    IdClass: 0, // the demo project (DemoSessions.Id), chosen from the list
    ParentsAvailable: false,
    Rating: 0,
    Details: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const projects = useDemoProjects('teacher');

  const completed = form.IdClassStatus === 0;
  const update = (patch) => {
    setForm((f) => ({ ...f, ...patch }));
    setError('');
  };

  // Escape closes (not while sending)
  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && !submitting && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [submitting, onClose]);

  // ---------- Submit (same checks + same payload) ----------
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (completed && !form.IdClass) return setError('Please choose the project.');
    if (completed && form.Rating === 0) return setError('Please rate the class.');
    if (completed && !form.Details.trim()) return setError('Please add your notes.');

    setSubmitting(true);
    setError('');
    try {
      await axios.post(`${API}/api/teacher/submitdemoform`, {
        ClassDateTime: feedback.ClassDateTime,
        IdClass: completed ? form.IdClass : 0,
        IdSchedule: feedback.IdSchedule,
        IdTeacher: feedback.IdTeacher,
        IdStudent: feedback.IdStudent,
        IdClassStatus: form.IdClassStatus,
        ParentsAvailable: completed ? (form.ParentsAvailable ? 1 : 0) : 0,
        Rating: completed ? form.Rating : 0,
        Details: form.Details,
      });
      onSubmitSuccess();
      onClose();
    } catch (err) {
      console.error('Error submitting demo feedback:', err);
      setError(err.response?.data?.error || 'We couldn’t send the feedback. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const name = [feedback.FirstName, feedback.LastName].filter(Boolean).join(' ');
  const when = new Date(feedback.ClassDateTime);

  return (
    <div className="rmfb-overlay" onClick={() => !submitting && onClose()}>
      <form
        className="rmfb"
        role="dialog"
        aria-modal="true"
        aria-labelledby="rmfb-title"
        onClick={(e) => e.stopPropagation()}
        onSubmit={handleSubmit}
        noValidate
      >
        <header className="rmfb-head">
          <div>
            <span className="rmfb-kind is-demo">Trial class</span>
            <h2 id="rmfb-title">{name || 'Trial feedback'}</h2>
            {!Number.isNaN(when.getTime()) && (
              <p>
                {when.toLocaleDateString('en-US', { weekday: 'short', day: 'numeric', month: 'short' })} ·{' '}
                {when.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </p>
            )}
          </div>
          <button type="button" className="rmfb-close" onClick={onClose} disabled={submitting} aria-label="Close">
            <FontAwesomeIcon icon={faXmark} />
          </button>
        </header>

        <div className="rmfb-body">
          <fieldset className="rmfb-field">
            <legend>How did the trial go?</legend>
            <div className="rmfb-chips">
              {STATUS.map((s) => (
                <label key={s.value} className={`rmfb-chip ${form.IdClassStatus === s.value ? 'is-on' : ''}`}>
                  <input
                    type="radio"
                    name="IdClassStatus"
                    checked={form.IdClassStatus === s.value}
                    onChange={() => update({ IdClassStatus: s.value })}
                    disabled={submitting}
                  />
                  {s.label}
                </label>
              ))}
            </div>
          </fieldset>

          {completed && (
            <>
              <div className="rmfb-field">
                <label className="rmfb-label" htmlFor="rmfb-project">
                  Project
                </label>
                <select
                  id="rmfb-project"
                  className="rmfb-input"
                  value={form.IdClass}
                  onChange={(e) => update({ IdClass: parseInt(e.target.value, 10) })}
                  disabled={submitting}
                >
                  <option value={0}>{projects.length ? 'Choose the project' : 'Loading projects…'}</option>
                  {[1, 2].map((pl) => {
                    const list = projects.filter((p) => p.plan === pl);
                    return list.length ? (
                      <optgroup key={pl} label={PLAN_LABEL[pl]}>
                        {list.map((p) => (
                          <option key={p.id} value={p.id}>
                            {p.name}
                            {p.details ? ` · ${p.details}` : ''}
                          </option>
                        ))}
                      </optgroup>
                    ) : null;
                  })}
                  {projects
                    .filter((p) => p.plan !== 1 && p.plan !== 2)
                    .map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name}
                      </option>
                    ))}
                </select>
              </div>

              <fieldset className="rmfb-field">
                <legend>Rate the class</legend>
                <div className="rmfb-stars" role="radiogroup" aria-label="Class rating">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button
                      key={n}
                      type="button"
                      role="radio"
                      aria-checked={form.Rating === n}
                      aria-label={`${n} star${n > 1 ? 's' : ''}`}
                      className={`rmfb-star ${n <= form.Rating ? 'is-on' : ''}`}
                      onClick={() => update({ Rating: n })}
                      disabled={submitting}
                    >
                      <FontAwesomeIcon icon={faStar} />
                    </button>
                  ))}
                </div>
              </fieldset>

              <label className={`rmfb-check ${form.ParentsAvailable ? 'is-on' : ''}`}>
                <input
                  type="checkbox"
                  checked={form.ParentsAvailable}
                  onChange={(e) => update({ ParentsAvailable: e.target.checked })}
                  disabled={submitting}
                />
                I talked with the parents
              </label>
            </>
          )}

          <div className="rmfb-field">
            <label className="rmfb-label" htmlFor="rmfb-notes">
              {completed ? 'Notes' : 'What happened? (optional)'}
            </label>
            <textarea
              id="rmfb-notes"
              className="rmfb-input rmfb-textarea"
              value={form.Details}
              onChange={(e) => update({ Details: e.target.value })}
              placeholder={completed ? 'Level, interests, which course fits…' : 'Any details for the sales team…'}
              disabled={submitting}
            />
          </div>

          {error && (
            <p className="rmfb-error" role="alert">
              {error}
            </p>
          )}
        </div>

        <footer className="rmfb-foot">
          <button type="button" className="rmfb-btn rmfb-btn--ghost" onClick={onClose} disabled={submitting}>
            Cancel
          </button>
          <button type="submit" className="rmfb-btn" disabled={submitting}>
            {submitting ? 'Sending…' : 'Submit feedback'}
          </button>
        </footer>
      </form>
    </div>
  );
};

export default DemoFeedbackModal;